import { z } from 'zod';
import { clpAmountSchema, httpUrlSchema, isoDateSchema, optionalText, quantitySchema, requiredText, skuSchema, } from '../common/coercion.js';
import { lenientEmailSchema } from '../common/email.js';
import { isValidRut } from '../common/rut.js';
/**
 * F6 — emision del DTE (documento tributario electronico).
 *
 * **Janis NO emite DTE chileno** (`POST /invoice` de JEF solo registra facturas
 * emitidas por otro), asi que el middleware se lo pide a un **facturador**
 * autorizado por el SII. El facturador es **Dot** (`./dot.js`); mientras su
 * conexion no este terminada, lo reemplaza un **simulador** que responde con
 * este mismo contrato. Cambiar uno por otro es implementar el puerto, no tocar
 * el flujo.
 *
 * Contrato NUESTRO entre el flujo y el facturador: estricto en las dos
 * direcciones.
 *
 * ## Lo que se guarda y se vuelve a leer
 *
 * Las formas internas que se persisten (el resultado del facturador, las lineas
 * emitidas de `dte_document`) siguen una regla: **estrictas al escribir, laxas
 * al leer** —cada una tiene su `…ReadSchema` con `passthrough`— y **solo
 * evolucionan agregando campos opcionales**. Asi una fila escrita por la version
 * de hoy sigue siendo legible manana, y una escrita manana no rompe al proceso
 * que todavia corre la de hoy.
 */
/** Codigos del SII de los dos documentos que emite una venta. */
export const DTE_DOCUMENT_TYPE = {
    /** Factura electronica: la venta a empresa, con RUT y razon social. */
    FACTURA: 33,
    /** Boleta electronica: la venta a consumidor final. */
    BOLETA: 39,
};
export const dteDocumentTypeSchema = z.union([z.literal(DTE_DOCUMENT_TYPE.FACTURA), z.literal(DTE_DOCUMENT_TYPE.BOLETA)], { errorMap: () => ({ message: 'El tipo de DTE debe ser 33 (factura) o 39 (boleta).' }) });
/**
 * Estado de un DTE en `dte_document`, en el orden en que avanza.
 *
 * 🔴 **Es monotono**: un documento nunca retrocede de estado. Cada paso del
 * flujo se salta si el documento ya llego a su estado o a uno posterior, y eso
 * es lo que hace seguro reintentar el pedido entero.
 *
 * - `issued`: el facturador devolvio folio. Es el unico efecto que no se puede
 *   repetir, y por eso se guarda antes de cualquier otro paso.
 * - `sap_reserved`: la factura de reserva quedo escrita en SAP.
 * - `custom_data_written`: el documento quedo en el `customData` del pedido de
 *   Janis. Aca termina el camino de inicio: JEF espera a `ready-for-invoice`.
 * - `janis_registered`: JEF registro la factura original.
 */
export const DTE_DOCUMENT_STATUSES = [
    'issued',
    'sap_reserved',
    'custom_data_written',
    'janis_registered',
];
export const dteDocumentStatusSchema = z.enum(DTE_DOCUMENT_STATUSES);
/**
 * Hasta donde llego la PARTE de un pedido en un DTE (`dte_order_share`,
 * migracion 0033 de la API), en el orden en que avanza: su `customData` y su
 * registro en JEF. En una venta agrupada (`docs/pedidos-de-tienda.md` §1.4) es
 * lo de cada hijo; en una venta de un pedido, la parte es el DTE entero.
 *
 * - `pending`: el DTE todavia no llego al `customData` del pedido.
 * - `custom_data_written` y `janis_registered`: los del DTE (ver arriba), pero
 *   del pedido.
 *
 * 🔴 Es la lista canonica: el CHECK de la base (`dte_order_share_status_check`),
 * la API (`DTE_ORDER_SHARE_STATUSES` de su schema) y la simulacion del picking
 * (`PICKING_SIMULATION_SHARE_STATUSES`) salen de aca. Un estado nuevo va primero
 * aca y en una migracion.
 */
export const DTE_ORDER_SHARE_STATUSES = [
    'pending',
    'custom_data_written',
    'janis_registered',
];
export const dteOrderShareStatusSchema = z.enum(DTE_ORDER_SHARE_STATUSES);
/** `true` si `status` ya llego a `min` o lo paso. Pura. */
export function isDteStatusAtLeast(status, min) {
    return DTE_DOCUMENT_STATUSES.indexOf(status) >= DTE_DOCUMENT_STATUSES.indexOf(min);
}
/** RUT canonico, sin puntos y con la K en mayuscula (`12345678-K`). */
const canonicalRutSchema = z
    .string()
    .regex(/^\d{1,8}-[\dK]$/, 'El RUT va sin puntos y con la K mayuscula (12345678-K).')
    // Un RUT invalido lo rechaza el SII, y puede hacerlo DESPUES de consumir el folio.
    .refine(isValidRut, 'El RUT no tiene un digito verificador valido.');
/**
 * Quien recibe el documento. En una boleta anonima es el RUT generico.
 *
 * Giro, direccion y comuna (AGREGADOS 2026-10-21, para las facturas del POS)
 * son obligatorios en factura ante el SII y los trae el POS en su sobre
 * (`../decorapos/dte.ts`). Llevan los nombres y los largos del SII que usa el
 * mapper de Dot (`apps/api/src/invoicing/dot/dot-invoice-xml.ts`:
 * `GiroRecep` 40, `DirRecep` 70, `CmnaRecep` 20), para no traducirlos dos
 * veces. Son opcionales porque una boleta no los lleva y porque las filas de
 * `dte_document.receiver` escritas antes no los tienen: solo se agregan campos.
 */
export const dteReceiverSchema = z
    .object({
    rut: canonicalRutSchema,
    /** Razon social en una factura; nombre del comprador en una boleta. */
    name: requiredText(100),
    email: optionalText(100),
    /** Giro del receptor (`GiroRecep`). */
    activity: optionalText(40),
    /** Calle y numero (`DirRecep`). */
    address: optionalText(70),
    /** Comuna (`CmnaRecep`). */
    commune: optionalText(20),
})
    .strict();
export const dteItemSchema = z
    .object({
    sku: skuSchema,
    description: optionalText(80),
    quantity: z
        .number({ invalid_type_error: 'La cantidad debe ser un numero.' })
        .positive('La cantidad debe ser mayor que cero.'),
    /** Precio unitario CON IVA, ya con el descuento aplicado: lo COBRADO por unidad. */
    unitPrice: clpAmountSchema,
    /**
     * Precio de LISTA por unidad, CON IVA, cuando la linea tiene rebaja
     * (AGREGADO 2026-10-27, la rebaja de linea del POS). El facturador lo
     * escribe en `PrcItem` y la rebaja, `(listUnitPrice − unitPrice) ×
     * quantity`, en `DescuentoMonto`: el cliente ve la lista y la rebaja, y la
     * linea (`MontoItem`) sigue valiendo lo cobrado. Pasarlos a neto es del
     * facturador (Dot, S8: `dot/dot-invoice-xml.ts`). Sin rebaja se omite.
     * `unitPrice` no cambia: el total, las NC y la foto de lo emitido siguen
     * usando lo cobrado.
     */
    listUnitPrice: clpAmountSchema.optional(),
})
    .strict();
/**
 * Identidad de una venta ante el facturador: `<origen>:<id en el origen>`.
 *
 * - `janis:<orderId>` para los pedidos que se leen de Janis.
 * - `decorapos:<ventaId>` para las ventas del POS: la usa `POST
 *   /api/decorapos/dte` y tambien F6 cuando el pedido es del canal del POS, asi
 *   que las dos puertas llegan al facturador con la MISMA clave y un pedido que
 *   entrara por las dos no produce dos documentos. `orderId` sigue siendo
 *   obligatorio y significa lo mismo: `/dte` espera a que el pedido exista en
 *   Janis antes de emitir.
 *
 * El origen va en minusculas y adelante para que dos origenes no puedan
 * producir la misma clave.
 */
export const dteSaleKeySchema = z
    .string()
    .regex(/^[a-z]+:[A-Za-z0-9:._-]{1,160}$/, 'La clave de la venta va como <origen>:<id> (p. ej. janis:6512f0a1c2b3d4e5f6a7b8c9).');
/** Lo que el flujo le pide al facturador. */
export const dteIssueRequestSchema = z
    .object({
    /**
     * 🔴 **La clave de idempotencia del facturador.** Un DTE emitido dos veces no
     * es un registro duplicado: son dos documentos tributarios ante el SII por
     * una sola venta, y anularlo exige una nota de credito. Por eso el flujo
     * guarda el resultado ANTES de seguir y no vuelve a pedirlo para la misma
     * venta, y el facturador responde lo ya emitido si recibe la misma clave.
     */
    saleKey: dteSaleKeySchema,
    /** Id del pedido en Janis. */
    orderId: requiredText(64),
    /** Id del pedido en el origen (el `_id` de Multivende). */
    commerceId: optionalText(64),
    documentType: dteDocumentTypeSchema,
    issueDate: isoDateSchema,
    currency: z.literal('CLP'),
    receiver: dteReceiverSchema,
    items: z.array(dteItemSchema).min(1, 'Un DTE sin lineas no documenta ninguna venta.'),
    /** El flete, como linea aparte. Esta en el total y no en las lineas. */
    shippingAmount: clpAmountSchema.optional(),
    /** Total CON IVA. Es lo que el cliente pago. */
    totalAmount: clpAmountSchema,
    /**
     * A quien envia el documento el facturador (AGREGADO 2026-10-21). Con la
     * regla de correo del POS: lo que el POS acepto no se bota en silencio.
     */
    emails: z
        .array(lenientEmailSchema)
        .min(1, 'Sin correos, el campo se omite.')
        .max(20, 'Hasta 20 correos por documento.')
        .optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    value.items.forEach((item, indice) => {
        if (item.listUnitPrice !== undefined && item.listUnitPrice <= item.unitPrice) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', indice, 'listUnitPrice'],
                message: `El precio de lista (${String(item.listUnitPrice)}) no es mayor que lo cobrado ` +
                    `(${String(item.unitPrice)}): sin rebaja, se omite.`,
            });
        }
    });
    const lineas = value.items.reduce((suma, item) => suma + item.unitPrice * item.quantity, 0);
    const calculado = Math.round(lineas + (value.shippingAmount ?? 0));
    if (calculado !== value.totalAmount) {
        // Un DTE que no cuadra lo rechaza el SII, y en el mejor caso lo rechaza
        // despues de haber consumido el folio.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['totalAmount'],
            message: `El total (${String(value.totalAmount)}) no cuadra con las lineas mas el flete ` +
                `(${String(calculado)}).`,
        });
    }
});
const dteIssuedLineShape = {
    /**
     * Id de la linea en el pedido de Janis. `null` porque un pedido que no creo
     * el middleware puede traer items sin id (`janisOrderItemSchema.id`). NO
     * representa el flete: el flete no es una linea, va en
     * `dte_document.shipping_amount`.
     */
    janisLineId: requiredText(64).nullable(),
    sku: skuSchema,
    /** Cantidad en unidades de Janis. Entera: Janis rechaza decimales. */
    quantity: z
        .number({ invalid_type_error: 'La cantidad debe ser un numero.' })
        .int('La cantidad de Janis es entera.')
        .positive('La cantidad debe ser mayor que cero.'),
    /**
     * Lo que comprometio la orden de venta de SAP:
     * `purchasedQuantity × sellingUnitMultiplier`. Admite decimales.
     */
    sapQuantity: quantitySchema,
    /** Precio unitario CON IVA por unidad de Janis: el mismo de la linea del DTE. */
    unitPrice: clpAmountSchema,
    /**
     * Venta agrupada (`docs/pedidos-de-tienda.md` §1.4): el pedido HIJO de Janis
     * al que pertenece la linea. Un DTE documenta la venta entera, y de aca salen
     * la parte de cada hijo (su registro en JEF) y las NC, que son siempre de un
     * hijo. Opcional: una venta de un pedido no lo necesita y las filas de antes
     * no lo tienen; se siguen leyendo igual (`dteIssuedLineReadSchema`).
     */
    janisOrderId: requiredText(64).optional(),
};
/**
 * Una linea tal como quedo emitida en el DTE: la foto que se guarda en
 * `dte_document.issued_lines`.
 *
 * Va alineada por indice con las lineas del DTE. Es la base de las notas de
 * credito: congela la cantidad de SAP para que una NC no dependa de lo que
 * `line_xref` diga despues.
 */
export const dteIssuedLineSchema = z.object(dteIssuedLineShape).strict();
/** La misma linea al RELEERLA de la base: conserva lo que no conoce. */
export const dteIssuedLineReadSchema = z.object(dteIssuedLineShape).passthrough();
/**
 * Las lineas emitidas de UN DTE, como se guardan (`dte_document.issued_lines`):
 * al menos una, cada una estricta, y `janisOrderId` en todas o en ninguna. En
 * una venta agrupada cada linea dice su hijo; una sin el no tendria parte en
 * JEF ni NC posible. Una venta de un pedido no lo lleva en ninguna.
 */
export const dteIssuedLinesSchema = z
    .array(dteIssuedLineSchema)
    .min(1, 'Un DTE emitido tiene al menos una linea.')
    .superRefine((lineas, ctx) => {
    if (lineas.every((linea) => linea.janisOrderId === undefined)) {
        return;
    }
    lineas.forEach((linea, index) => {
        if (linea.janisOrderId === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'janisOrderId'],
                message: 'janisOrderId va en todas las lineas emitidas o en ninguna: en una venta agrupada ' +
                    'cada linea dice su pedido hijo.',
            });
        }
    });
});
/** El DTE quedo emitido: hay folio y documento. */
export const dteIssuedSchema = z
    .object({
    status: z.literal('issued'),
    /** Quien lo emitio (`simulated` mientras no haya facturador real). */
    issuer: requiredText(32),
    documentType: dteDocumentTypeSchema,
    folio: z.number().int().positive('El folio debe ser mayor que cero.'),
    issueDate: isoDateSchema,
    totalAmount: clpAmountSchema,
    /** URL del PDF. Es lo que se registra en Janis y despues se sube al canal. */
    url: httpUrlSchema,
})
    .strict();
/**
 * El facturador rechazo el documento.
 *
 * No es un fallo de red: es una respuesta definitiva, y reintentarla devuelve
 * lo mismo. El flujo avisa a Janis (`invoice-failed`) y deja la venta visible.
 */
export const dteRejectedSchema = z
    .object({
    status: z.literal('rejected'),
    issuer: requiredText(32),
    reason: requiredText(500),
})
    .strict();
export const dteIssueResultSchema = z.discriminatedUnion('status', [
    dteIssuedSchema,
    dteRejectedSchema,
]);
/**
 * El resultado del facturador al RELEERLO de la base (`issuer_response`).
 *
 * Laxo en las claves: un campo opcional que el facturador agregue despues no
 * puede dejar ilegibles los resultados que ya estan guardados.
 */
export const dteIssueResultReadSchema = z.discriminatedUnion('status', [
    dteIssuedSchema.passthrough(),
    dteRejectedSchema.passthrough(),
]);
//# sourceMappingURL=dte.js.map